import { useState, useEffect, useRef, useCallback } from "react";
import { startDownload, getStatus, getResults, exportZipUrl } from "./api";
import UrlInput        from "./components/UrlInput";
import ProgressTracker from "./components/ProgressTracker";
import Spinner         from "./components/Spinner";
import ErrorBanner     from "./components/ErrorBanner";
import MediaGallery    from "./components/MediaGallery";
import MetadataCard    from "./components/MetadataCard";
import AnalysisCard    from "./components/AnalysisCard";
import HistoryPanel, { addHistoryEntry } from "./components/HistoryPanel";
import TranscriptSection from "./components/TranscriptSection";

const POLL_INTERVAL = 2000;

const PHASE = {
  IDLE:       "idle",
  PROCESSING: "processing",
  DONE:       "done",
  ERROR:      "error",
};

// ── Instagram gradient icon ──────────────────────────────────────────────────
function IgIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-9 h-9" fill="none">
      <defs>
        <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#f09433" />
          <stop offset="25%"  stopColor="#e6683c" />
          <stop offset="50%"  stopColor="#dc2743" />
          <stop offset="75%"  stopColor="#cc2366" />
          <stop offset="100%" stopColor="#bc1888" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="6"
            stroke="url(#ig)" strokeWidth="2"/>
      <circle cx="12" cy="12" r="4" stroke="url(#ig)" strokeWidth="2"/>
      <circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig)"/>
    </svg>
  );
}

// ── Main app ─────────────────────────────────────────────────────────────────
export default function App() {
  const [phase,     setPhase]     = useState(PHASE.IDLE);
  const [jobId,     setJobId]     = useState(null);
  const [jobStatus, setJobStatus] = useState({ status: "queued", progress: 0 });
  const [results,   setResults]   = useState(null);
  const [error,     setError]     = useState("");
  const [historyVer, setHistoryVer] = useState(0);   // incremented to re-render history
  const pollRef = useRef(null);

  // ── helpers ────────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const bumpHistory = () => setHistoryVer((v) => v + 1);

  // ── polling loop ───────────────────────────────────────────────────────────
  const startPolling = useCallback((id, submittedUrl) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await getStatus(id);
        setJobStatus(s);

        if (s.status === "complete") {
          stopPolling();
          const r = await getResults(id);
          setResults(r);
          setPhase(PHASE.DONE);
          addHistoryEntry({
            job_id:       id,
            url:          submittedUrl,
            status:       "complete",
            username:     r?.summary?.username      || "",
            content_type: r?.summary?.content_type  || "",
            files_count:  r?.summary?.files_count   || 0,
            created_at:   new Date().toISOString(),
          });
          bumpHistory();
        } else if (s.status === "failed") {
          stopPolling();
          setError(s.error || "Pipeline failed with no message.");
          setPhase(PHASE.ERROR);
          addHistoryEntry({
            job_id:     id,
            url:        submittedUrl,
            status:     "failed",
            created_at: new Date().toISOString(),
          });
          bumpHistory();
        }
      } catch {
        stopPolling();
        setError("Lost connection to the API on port 8000. Is the backend running?");
        setPhase(PHASE.ERROR);
      }
    }, POLL_INTERVAL);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── submit new URL ─────────────────────────────────────────────────────────
  const handleSubmit = async (url) => {
    setPhase(PHASE.PROCESSING);
    setError("");
    setResults(null);
    setJobStatus({ status: "queued", progress: 0 });
    try {
      const { job_id } = await startDownload(url);
      setJobId(job_id);
      startPolling(job_id, url);
    } catch (e) {
      if (e?.response?.status === 429) {
        setError("API rate limit reached (5 requests / minute). Please wait a moment.");
      } else {
        const detail = e?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : detail?.msg || e.message || "Could not reach the API."
        );
      }
      setPhase(PHASE.ERROR);
    }
  };

  // ── re-open a history entry ────────────────────────────────────────────────
  const handleReopen = useCallback(async (entry) => {
    setPhase(PHASE.PROCESSING);
    setError("");
    setResults(null);
    setJobStatus({ status: "complete", progress: 100 });
    setJobId(entry.job_id);
    try {
      const r = await getResults(entry.job_id);
      setResults(r);
      setPhase(PHASE.DONE);
    } catch {
      setError("Could not reload results — the server may have restarted and lost this job.");
      setPhase(PHASE.ERROR);
    }
  }, []);

  // ── reset to idle ──────────────────────────────────────────────────────────
  const handleReset = () => {
    stopPolling();
    setPhase(PHASE.IDLE);
    setJobId(null);
    setResults(null);
    setError("");
    setJobStatus({ status: "queued", progress: 0 });
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const dl          = results?.download;
  const files       = dl?.files            || [];
  const transcripts = results?.transcripts || [];
  const analysis    = results?.analysis;
  const summary     = results?.summary;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="max-w-5xl mx-auto px-4 py-12 space-y-12">

        {/* ─── Header ──────────────────────────────────────────────────── */}
        <header className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-1">
            <IgIcon />
            <h1 className="text-3xl font-bold tracking-tight">Instagram Downloader</h1>
          </div>
          <p className="text-[#555] text-base">
            Download, transcribe, and AI-analyse any Instagram content
          </p>
        </header>

        {/* ─── URL Input ───────────────────────────────────────────────── */}
        <UrlInput onSubmit={handleSubmit} loading={phase === PHASE.PROCESSING} />

        {/* ─── PROCESSING ──────────────────────────────────────────────── */}
        {phase === PHASE.PROCESSING && (
          <section className="flex flex-col items-center gap-8 py-4">
            <Spinner size="lg" />
            <ProgressTracker
              status={jobStatus.status}
              progress={jobStatus.progress}
            />
          </section>
        )}

        {/* ─── ERROR ───────────────────────────────────────────────────── */}
        {phase === PHASE.ERROR && (
          <ErrorBanner error={error} onDismiss={handleReset} />
        )}

        {/* ─── RESULTS ─────────────────────────────────────────────────── */}
        {phase === PHASE.DONE && results && (
          <section className="space-y-8">

            {/* Summary banner */}
            {summary && (
              <div className="flex flex-wrap items-center gap-3 px-5 py-4 bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl">
                {summary.username && (
                  <span className="font-semibold text-indigo-300">@{summary.username}</span>
                )}
                {summary.content_type && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#2e2e2e] text-[#aaa] capitalize">
                    {summary.content_type}
                  </span>
                )}
                <span className="text-sm text-[#555]">
                  {summary.files_count} file{summary.files_count !== 1 ? "s" : ""}
                  {summary.videos_count > 0 &&
                    ` · ${summary.videos_count} video${summary.videos_count !== 1 ? "s" : ""}`}
                  {summary.has_transcript && " · transcribed"}
                  {summary.has_analysis   && " · analysed"}
                </span>
                <div className="ml-auto flex gap-3 items-center">
                  <button
                    onClick={handleReset}
                    className="text-sm text-[#555] hover:text-white transition-colors"
                  >
                    ← New download
                  </button>
                  <a
                    href={exportZipUrl(jobId)}
                    download
                    className="px-4 py-1.5 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 transition-colors"
                  >
                    ↓ ZIP
                  </a>
                </div>
              </div>
            )}

            <MediaGallery files={files} transcripts={transcripts} />

            {/* Full transcript section — always visible, one card per video */}
            <TranscriptSection transcripts={transcripts} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MetadataCard download={dl} />
              <AnalysisCard analysis={analysis} />
            </div>

            <div className="flex justify-center pb-4">
              <a
                href={exportZipUrl(jobId)}
                download
                className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold bg-indigo-600 hover:bg-indigo-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m-4-4l4 4 4-4"/>
                </svg>
                Download ZIP — all media + transcripts + report
              </a>
            </div>
          </section>
        )}

        {/* ─── HISTORY (idle & error states only) ──────────────────────── */}
        {(phase === PHASE.IDLE || phase === PHASE.ERROR) && (
          <HistoryPanel key={historyVer} onReopen={handleReopen} />
        )}

      </div>
    </div>
  );
}
